import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useBusinesses } from "@finitless/shared/hooks";
import { Icon, Input } from "../../ui";
import Pagination from "../../ui/Data/Pagination";
import { cn } from "@/lib/utils";
import {
  META_ADS_BUSINESS_PICKER_PAGE_SIZE,
  META_ADS_BUSINESS_SEARCH_DEBOUNCE_MS,
} from "./metaAdsUtils";

/**
 * Paginated list of businesses (name + logo) for Meta Ads campaign store selection, with debounced search.
 * @param {object|null} [pinnedRow] — Full business row to show first when it matches `value` but is missing from the current page (e.g. edit form hydration).
 */
export function MetaAdsBusinessPicker({
  value,
  onSelect,
  onMetaChange,
  className,
  pinnedRow = null,
}) {
  const { t } = useTranslation("metaAds");
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchInput.trim());
    }, META_ADS_BUSINESS_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const hasSearch = debouncedSearchTerm.trim() !== "";

  /** Unfiltered account size for parent layout (keeps the section visible when a search has 0 matches). */
  const { data: accountScopeData, isLoading: accountScopeLoading } =
    useBusinesses({
      enabledFilter: "enabled",
      searchTerm: "",
      page: 1,
      pageSize: 1,
      includePagination: true,
      includeWhere: false,
      fields: "id,logo,name,enabled",
    });

  /**
   * No search: same as before — `includeWhere: false` (no API filter, all stores).
   * With search: `includeWhere: true` with name/email/phone ilike. Use `all` for enabled
   * so the same businesses are searchable as are listed when search is empty.
   */
  const { data, isLoading, isFetching } = useBusinesses({
    enabledFilter: hasSearch ? "all" : "enabled",
    searchTerm: debouncedSearchTerm,
    page,
    pageSize: META_ADS_BUSINESS_PICKER_PAGE_SIZE,
    includePagination: true,
    includeWhere: hasSearch,
    fields: "id,logo,name,enabled",
  });

  const businesses = data?.businesses ?? [];
  const pagination = data?.pagination ?? {};
  const totalPages = Math.max(1, pagination.total_pages || 1);
  const accountTotal = accountScopeData?.pagination?.total ?? 0;

  const rowsToRender = useMemo(() => {
    const list = [...businesses];
    if (
      pinnedRow &&
      value != null &&
      String(value).trim() !== "" &&
      String(pinnedRow.id) === String(value) &&
      !list.some((b) => String(b.id) === String(value))
    ) {
      return [pinnedRow, ...list];
    }
    return list;
  }, [businesses, pinnedRow, value]);

  useEffect(() => {
    onMetaChange?.({
      total: accountTotal,
      isLoading: accountScopeLoading || isLoading || isFetching,
    });
  }, [accountScopeLoading, accountTotal, isLoading, isFetching, onMetaChange]);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (page > totalPages) {
      setPage(Math.max(1, totalPages));
    }
  }, [isLoading, page, totalPages]);

  return (
    <div className={cn("space-y-3", className)}>
      <Input
        type="search"
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        placeholder={t("create.searchBusinessesPlaceholder")}
        variant="dark"
        size="lg"
        className="w-full"
        aria-label={t("create.searchBusinessesLabel")}
        leftIcon={<Icon name="Search" className="h-4 w-4 text-white/50" />}
        rightIcon={
          searchInput ? (
            <button
              type="button"
              onClick={() => {
                setSearchInput("");
              }}
              className="text-muted-foreground rounded p-0.5 transition hover:text-white"
              aria-label={t("create.searchBusinessesClear")}
            >
              <Icon name="X" className="h-4 w-4" />
            </button>
          ) : undefined
        }
      />
      <div
        className="space-y-2"
        role="listbox"
        aria-label={t("create.selectBusiness")}
      >
        {isLoading && rowsToRender.length === 0 ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-16 animate-pulse rounded-xl border border-white/5 bg-white/[0.04]"
              />
            ))}
          </div>
        ) : rowsToRender.length === 0 && hasSearch ? (
          <p className="text-muted-foreground text-sm">
            {t("create.searchBusinessesNoResults")}
          </p>
        ) : rowsToRender.length === 0 ? null : (
          rowsToRender.map((b) => {
            const selected = String(value) === String(b.id);
            const isEnabled = b.enabled !== false;
            return (
              <button
                key={b.id}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => onSelect(b)}
                className={cn(
                  "flex w-full min-w-0 items-center gap-3 rounded-xl border p-3 text-left transition",
                  selected
                    ? "border-brand-cyan/50 bg-brand-cyan/10 ring-brand-cyan/20 ring-1"
                    : "border-border bg-muted/20 hover:border-muted-foreground/30",
                )}
              >
                {b.logo ? (
                  <img
                    src={b.logo}
                    alt=""
                    className="h-11 w-11 flex-shrink-0 rounded-lg object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <div className="bg-muted flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg border border-white/10">
                    <Icon name="Store" className="h-5 w-5 text-white/45" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="min-w-0 flex-1 truncate font-medium text-white">
                      {b.name || `Business ${b.id}`}
                    </span>
                    <span
                      className={cn(
                        "inline-flex flex-shrink-0 items-center rounded-full border px-2 py-0.5 text-xs leading-none font-medium",
                        isEnabled
                          ? "border-emerald-500/35 bg-emerald-500/15 text-emerald-200"
                          : "border-white/15 bg-white/[0.06] text-white/60",
                      )}
                    >
                      {isEnabled
                        ? t("create.businessBadgeEnabled")
                        : t("create.businessBadgeDisabled")}
                    </span>
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>

      {totalPages > 1 && (
        <div className="pt-1">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            maxVisiblePages={5}
            className="!space-x-1"
          />
        </div>
      )}
    </div>
  );
}

export default MetaAdsBusinessPicker;
